import { Cell, Grid } from 'bold-ui'
import { useListFieldStatus } from 'components/form'
import { Form, FormRenderProps } from 'components/form/core/Form'
import { CiapSelectField, CiapSelectFieldModel } from 'components/form/field/select/CiapSelectField/CiapSelectField'
import { CiapCapituloEnum } from 'graphql/types.generated'
import { useCallback } from 'react'
import { MetaArray, metaPath } from 'util/metaPath'
import { isEmpty } from 'util/validation/Util'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { EditableList, useEditableListField } from '../../../components/EditableList'
import { CiapPlanoModel } from '../model-plano'
import { ciapPlanoValidator } from '../validator-plano'
import { CiapRow } from './CiapRow'

const meta = metaPath<CiapPlanoModel>()

export interface CiapPlanoProps {
  name: MetaArray<CiapPlanoModel>
  cidadao?: CidadaoAtendimento
  controlModifiedStatus?: boolean
}

export function CiapPlanoField(props: Readonly<CiapPlanoProps>) {
  const { name, cidadao, controlModifiedStatus = false } = props

  const {
    handleRowChanged,
    handleSubmit,
    input: { value },
    removeItem,
    resetItemToInitialValue,
  } = useEditableListField({ name, isSameRow })

  const { fieldStatus, disabledValues: ciapsDisabled } = useListFieldStatus({
    name,
    controlModifiedStatus,
    isSameRow,
  })

  const ciapIsEqual = (ciap: CiapSelectFieldModel) => {
    return (value || []).some((o2: CiapPlanoModel) => ciap.id === o2.ciap.id)
  }

  const itemIsDisabled = useCallback(
    (item: CiapSelectFieldModel) => ciapsDisabled?.map((itemDisabled) => itemDisabled?.ciap.id).includes(item.id),
    [ciapsDisabled]
  )

  const showCiapsSemStatus = !controlModifiedStatus && value?.length > 0
  const showCiapsComStatus = controlModifiedStatus && fieldStatus?.size > 0

  const renderForm = (formProps: FormRenderProps<CiapPlanoModel>) => {
    const handleChange = (newValue: CiapSelectFieldModel) => {
      newValue && formProps.form.submit()
    }

    return (
      <CiapSelectField
        label='CIAP 2'
        onChange={handleChange}
        name={meta.ciap}
        itemIsEqual={ciapIsEqual}
        sexo={isEmpty(cidadao?.identidadeGeneroDbEnum) ? cidadao?.sexo : undefined}
        capitulosIncluidos={[CiapCapituloEnum.PROCEDIMENTOS]}
        menuMinWidth={620}
        excluirCIAPsDAB
        fetchCID10Encaminhamento
        dataTestId='CiapPlano'
        itemIsDisabled={!isEmpty(ciapsDisabled) ? itemIsDisabled : undefined}
      />
    )
  }

  return (
    <Grid>
      <Cell size={8}>
        <Form<CiapPlanoModel> render={renderForm} validate={ciapPlanoValidator} onSubmit={handleSubmit} />
      </Cell>
      {showCiapsSemStatus && (
        <Cell size={12}>
          <EditableList>
            {value.map((item: CiapPlanoModel) => (
              <CiapRow key={item._id} model={item} onChange={handleRowChanged} onRemove={removeItem} />
            ))}
          </EditableList>
        </Cell>
      )}

      {showCiapsComStatus && (
        <Cell size={12}>
          <EditableList>
            {Array.from(fieldStatus, ([item, status]) => (
              <CiapRow
                key={item._id}
                model={item}
                onRemove={removeItem}
                onReset={resetItemToInitialValue}
                status={status}
                onChange={handleRowChanged}
                controlModifiedStatus
              />
            ))}
          </EditableList>
        </Cell>
      )}
    </Grid>
  )
}

const isSameRow = (itemA: CiapPlanoModel, itemB: CiapPlanoModel) => itemA.ciap.id === itemB.ciap.id
